import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  useMySubscriptions, usePauseSubscription, useDeactivateSubscription,
  describeSchedule, type SubscriptionWithJoins,
} from "@/lib/subscriptions";
import { PAYMENT_LABEL } from "@/lib/orders";
import { formatDateIso } from "@/lib/calendar";

const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
const dfmt = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

export default function MeinAboPage() {
  const { data: subs = [], isLoading } = useMySubscriptions();
  const active = subs.filter((s) => s.is_active);
  const inactive = subs.filter((s) => !s.is_active);

  if (isLoading) return <div className="card">Lade…</div>;

  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-brand font-bold text-wappen-700">Mein Abo</h1>

      {subs.length === 0 ? (
        <div className="card text-center space-y-3">
          <div className="text-5xl">📅</div>
          <p className="text-hof-700">Sie haben noch kein Abo. Lassen Sie sich Lieblings-Produkte regelmäßig liefern!</p>
          <Link to="/shop" className="btn-primary inline-block">Zum Hofladen</Link>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-xl font-semibold text-hof-800">Aktive Abos</h2>
              {active.map((s) => <AboCard key={s.id} s={s} />)}
            </section>
          )}
          {inactive.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-xl font-semibold text-hof-600">Beendete Abos</h2>
              {inactive.map((s) => <AboCard key={s.id} s={s} readOnly />)}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function AboCard({ s, readOnly = false }: { s: SubscriptionWithJoins; readOnly?: boolean }) {
  const pause = usePauseSubscription();
  const deactivate = useDeactivateSubscription();
  const [showPause, setShowPause] = useState(false);

  const productIds = s.items.map((i) => i.product_id);
  const { data: productMap = new Map() } = useQuery({
    queryKey: ["product-names", productIds.join(",")],
    enabled: productIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase.from("products").select("id,name,price_cents").in("id", productIds);
      return new Map((data ?? []).map((p) => [p.id, p]));
    },
  });

  const isPaused = !!s.paused_until && new Date(s.paused_until) >= new Date();
  const subtotal = s.items.reduce((acc, i) => {
    const p = productMap.get(i.product_id);
    return acc + (p?.price_cents ?? 0) * i.qty;
  }, 0);

  return (
    <article className="card space-y-3">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="font-semibold text-hof-900">{describeSchedule(s)}</div>
          {isPaused && (
            <span className="pill-warn mt-1 inline-block">Pausiert bis {dfmt.format(new Date(s.paused_until!))}</span>
          )}
        </div>
        <div className="text-right">
          <div className="text-sm text-hof-500">je Lieferung</div>
          <div className="text-xl font-bold text-wiese-700">{fmt.format(subtotal / 100)}</div>
        </div>
      </div>

      <div className="text-sm text-hof-700">
        <div>{PAYMENT_LABEL[s.payment_method]}</div>
        {s.address && <div>Lieferung an: {s.address.street}, {s.address.zip} {s.address.city}</div>}
      </div>

      <ul className="text-sm text-hof-800 space-y-1">
        {s.items.map((i) => {
          const p = productMap.get(i.product_id);
          return (
            <li key={i.id}>
              {i.qty}× {p?.name ?? "—"}
            </li>
          );
        })}
      </ul>

      {!readOnly && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-hof-100">
          {!isPaused ? (
            <button onClick={() => setShowPause(true)} className="btn-secondary">Pausieren</button>
          ) : (
            <button onClick={() => pause.mutate({ id: s.id, from: null, until: null })} className="btn-secondary">
              Pause aufheben
            </button>
          )}
          <button onClick={() => {
            if (confirm("Abo wirklich beenden? (Keine weiteren Lieferungen)")) deactivate.mutate(s.id);
          }} className="btn-danger ml-auto">Beenden</button>
        </div>
      )}

      {showPause && (
        <PauseDialog
          subscriptionId={s.id}
          onClose={() => setShowPause(false)}
          onConfirm={(from, until) => {
            pause.mutate({ id: s.id, from, until });
            setShowPause(false);
          }}
        />
      )}
    </article>
  );
}

function PauseDialog({ subscriptionId: _id, onClose, onConfirm }: {
  subscriptionId: string;
  onClose: () => void;
  onConfirm: (from: string, until: string) => void;
}) {
  const today = formatDateIso(new Date());
  const [from, setFrom] = useState(today);
  const [until, setUntil] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return formatDateIso(d);
  });
  return (
    <div className="fixed inset-0 z-50 bg-hof-900/40 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-md space-y-3">
        <h3 className="text-xl font-bold text-hof-900">Abo pausieren</h3>
        <p className="text-sm text-hof-600">In diesem Zeitraum erfolgt keine Lieferung — z.B. während Urlaub.</p>
        <div>
          <label className="label">Von</label>
          <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="label">Bis einschließlich</label>
          <input type="date" className="input" value={until} onChange={(e) => setUntil(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <button onClick={() => onConfirm(from, until)} className="btn-primary flex-1">Pause aktivieren</button>
          <button onClick={onClose} className="btn-secondary">Abbrechen</button>
        </div>
      </div>
    </div>
  );
}
